/**
 * Cycle Workflow Behavior Types
 * ==============================
 *
 * Defines the data shapes returned by the CycleRuleEngine backend endpoint.
 *
 * HARD-STOP RULES (global – non-negotiable):
 *   - These types are for DISPLAY purposes only.
 *   - The frontend MUST NOT derive any monetary calculations from these values.
 *   - All financial computations remain on the server (EntitlementEngineService).
 *   - Cycle duration is always DAY-BASED.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Workflow Mode Enum
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The operational mode that governs a specific cycle.
 *
 * Standard         – no hybrid invoice/FOC rules; normal assisted workflow.
 * InvoiceValidation – cycles 1 to InvoiceValidationCycleLimit; invoice upload
 *                    required; monetary fields hidden.
 * FocAssistance    – cycles >= FocEligibilityStartCycle; patient is FOC-eligible;
 *                    assistance workflow activates automatically.
 */
export type CycleWorkflowMode = 'Standard' | 'InvoiceValidation' | 'FocAssistance';

// ─────────────────────────────────────────────────────────────────────────────
// API Response Shape
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolved per-cycle behaviour rules returned from
 * GET /programs/{programId}/cycles/{cycleNumber}/workflow-behavior
 *
 * The frontend consumes this to:
 *   - Show or hide monetary / financial fields (isMonetaryTrackingDisabled).
 *   - Render the invoice-upload requirement banner (invoiceUploadRequired).
 *   - Display the FOC eligibility transition badge (isFocEligible).
 *   - Drive conditional form sections in Patient360 and utilization pages.
 *
 * HARD-STOP: No financial values must ever be computed on the frontend from this type.
 */
export interface CycleBehaviorResult {
  programId: string;
  cycleNumber: number;

  /** One of: 'Standard' | 'InvoiceValidation' | 'FocAssistance' */
  workflowMode: CycleWorkflowMode;

  /** True when the cycle is in the invoice validation phase (1 to invoiceValidationCycleLimit). */
  isInvoiceValidationPhase: boolean;

  /** True when the patient has become FOC-eligible (cycle >= focEligibilityStartCycle). */
  isFocEligible: boolean;

  /** True when a physical invoice document upload is mandatory for this cycle. */
  invoiceUploadRequired: boolean;

  /**
   * True when the program configuration disables all patient monetary amount tracking.
   * When true, monetary/financial fields MUST NOT be rendered.
   */
  isMonetaryTrackingDisabled: boolean;

  /**
   * True when the program configuration disables reimbursement amount tracking.
   * When true, reimbursement settlement fields MUST NOT be rendered.
   */
  isReimbursementTrackingDisabled: boolean;

  /** Last cycle number that falls within the invoice-validation phase (inclusive). */
  invoiceValidationCycleLimit: number;

  /** First cycle number from which FOC assistance becomes active. */
  focEligibilityStartCycle: number;

  /** Human-readable description of the active rules for this cycle. */
  summary: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Duplicate Invoice Check
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Result of checking whether an invoice already exists for a given cycle.
 * Returned from GET /programs/{programId}/cases/{caseId}/cycle-invoices/{cycleNumber}/duplicate-check
 */
export interface CycleInvoiceDuplicateCheckResult {
  hasDuplicate: boolean;
  existingInvoiceId?: string;
  existingInvoiceNumber?: string;
  existingInvoiceCreatedAt?: string;
  message: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// UI Helpers
// ─────────────────────────────────────────────────────────────────────────────

export const CYCLE_WORKFLOW_MODE_LABELS: Record<CycleWorkflowMode, string> = {
  Standard: 'Standard workflow',
  InvoiceValidation: 'Invoice validation required',
  FocAssistance: 'FOC assistance eligible',
};

export const CYCLE_WORKFLOW_MODE_DESCRIPTIONS: Record<CycleWorkflowMode, string> = {
  Standard: 'This cycle uses the standard assisted workflow. No special invoice or FOC rules apply.',
  InvoiceValidation:
    'This cycle requires a valid invoice to be uploaded and verified before progression. ' +
    'No monetary tracking occurs during this phase.',
  FocAssistance:
    'The patient has completed the required paid cycles and is now eligible for ' +
    'Free-of-Charge (FOC) support. The assistance workflow activates automatically.',
};

/**
 * Returns true when financial/monetary fields should be hidden for the given behavior result.
 * Always server-driven — the frontend never computes this independently.
 */
export function shouldHideMonetaryFields(behavior: CycleBehaviorResult | null | undefined): boolean {
  if (!behavior) return false;
  return behavior.isMonetaryTrackingDisabled || behavior.isReimbursementTrackingDisabled;
}

/**
 * Returns true when invoice upload is required and not yet satisfied.
 */
export function isInvoiceUploadPending(
  behavior: CycleBehaviorResult | null | undefined,
  hasUploadedInvoice: boolean,
): boolean {
  if (!behavior) return false;
  return behavior.invoiceUploadRequired && !hasUploadedInvoice;
}
